import React from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import fuzzyFilterFactory from 'react-fuzzy-filter';
import { CSSTransitionGroup } from 'react-transition-group';
import PaginatedList from '../../components/PaginatedList';
import ListFilterBox from '../../components/ListFilterBox';
import SelectDropdown from '../../components/SelectDropdown';

const ControlHeader = props => (
  <Row className="tools">
    <Col sm={4} className="table-header">
      <ListFilterBox
        onChange={props.filterBoxChange}
        fuzzyFilterFactory={props.fuzzyFilterFactory}
      />
    </Col>
    <Col sm={5} className="table-header" />
    <Col sm={3} className="table-header">
      <Button
        bsStyle="primary"
        type="submit"
        className="relative-padding"
        onClick={props.addButtonClick}
      >
        Add Agency
      </Button>
    </Col>
  </Row>
);

const AddNewRow = props => (
  <Row>
    <Col sm={4} className="table-entry">
      <SelectDropdown
        onChange={props.selectOnChange}
        value={props.selectValue}
        options={props.items}
        aria-label="Agency Select Box"
        keys={['agencyId', 'agencyName']}
        focus={true}
      />
    </Col>
    <Col sm={4} className="table-entry">
      <div className="checkbox-wrapper agency">
        <input type="checkbox" id="isAdmin" onChange={props.checkboxOnChange} />
        <label htmlFor="isAdmin">Admin access?</label>
      </div>
    </Col>
    <Col sm={2} className="table-entry">
      <Button
        className="relative-padding"
        bsStyle="default"
        onClick={props.addItem}
      >
        Add
      </Button>
    </Col>
    <Col sm={2} className="table-entry">
      <Button
        bsStyle="link"
        className="relative-padding"
        onClick={props.cancel}
      >
        Cancel
      </Button>
    </Col>
  </Row>
);

const LineItem = props => {
  const agency = props.item;
  const isAdmin = agency.permissions.find(a => a.permissionName === 'Admin');
  return (
    <Row key={agency.agencyId}>
      <Col sm={4} className="table-entry">
        {agency.agencyName}
      </Col>
      <Col sm={3} smOffset={3} className="table-entry">
        {isAdmin && <p>Administrator</p>}
      </Col>
      <Col sm={2} className="table-entry">
        {!agency.inheritedFromParent && (
          <Button
            className="action-button"
            bsStyle="link"
            onClick={() => props.removeItem(agency)}
            aria-label={`Remove ${agency.agencyName}`}
          >
            Remove
          </Button>
        )}
      </Col>
    </Row>
  );
};

class CustomerAgencies extends React.Component {
  static displayName = 'CustomerAgencies';

  constructor(props) {
    super(props);
    this.state = {
      newAgencyId: undefined,
      showNewAgency: false,
      filterValue: ''
    };
    this.fuzzyFilterFactory = fuzzyFilterFactory();
    this.timeoutId = undefined;
  }

  componentDidMount() {
    // get agencies
    const customerId = parseInt(this.props.params.customerId, 10);
    this.props.getCustomerAgencies(customerId);

    // get unallocated agencies and permissions
    this.props.getAgencySettings(customerId);

    this.screenReaderContent.focus();
  }

  componentWillUnmount() {
    // clear timeout to prevent setState on unmounted component
    clearTimeout(this.timeoutId);
  }

  removeAgency = agency => {
    this.setState({
      isSaving: true,
      errorMsg: undefined,
      successMsg: undefined
    });

    const customerId = parseInt(this.props.params.customerId, 10);
    this.props
      .removeAgencyFromCustomer(customerId, agency)
      .then(response => this.success(response), error => this.failure(error));
  };

  addAgency = () => {
    const { newAgencyId, isAdmin, newAgencyName } = this.state;
    const customerId = parseInt(this.props.params.customerId, 10);
    if (newAgencyId) {
      this.setState({
        isSaving: true,
        errorMsg: undefined,
        successMsg: undefined
      });

      this.props
        .addAgencyToCustomer(customerId, newAgencyId, newAgencyName, isAdmin)
        .then(response => this.success(response), error => this.failure(error));
    }
    this.setState({
      showNewAgency: false,
      newAgencyId: undefined,
      isAdmin: undefined
    });
  };

  success = response => {
    this.setState({
      isSaving: false,
      errorMsg: undefined,
      successMsg: 'Success!'
    });
    this.timeoutId = setTimeout(
      () => this.setState({ successMsg: undefined }),
      3000
    );
  };

  failure = error => {
    this.setState({ isSaving: false, errorMsg: error.data.message });
  };

  updateFilterValue = filterValue => {
    this.setState({ filterValue: filterValue || '' });
  };

  addButtonClick = () => {
    this.setState({ showNewAgency: !this.state.showNewAgency });
  };

  cancel = () => {
    this.setState({ showNewAgency: false, newAgencyId: undefined });
  };

  selectOnChange = (id, name) => {
    this.setState({
      newAgencyId: id,
      newAgencyName: name
    });
  };

  checkboxOnChange = event => {
    this.setState({ isAdmin: event.target.checked });
  };

  render() {
    const unallocatedAgencies = this.props.currentCustomer.agencySettings
      .unallocatedAgencies;

    const allocatedAgencies = this.props.currentCustomer.agencies;
    const LineItemWrapper = props => (
      <LineItem {...props} removeItem={this.removeAgency} />
    );

    return (
      <div>
        <Row />
        <h3
          className="screen-reader-only"
          tabIndex="-1"
          ref={i => (this.screenReaderContent = i)}
        >
          Showing agencies with access to customer{' '}
          {this.props.currentCustomer.name}
        </h3>

        <ControlHeader
          filterBoxChange={this.updateFilterValue}
          fuzzyFilterFactory={this.fuzzyFilterFactory}
          addButtonClick={this.addButtonClick}
        />

        <Row className="row-header">
          <Col sm={4} className="table-header">
            Agency name
          </Col>
          <Col sm={3} smOffset={3} className="table-header">
            Administrator
          </Col>
          <Col sm={2} className="table-header">
            Action
          </Col>
          <Col sm={12}>
            <hr />
          </Col>
        </Row>

        {this.state.showNewAgency && (
          <AddNewRow
            items={unallocatedAgencies}
            addItem={this.addAgency}
            selectValue={this.state.newAgencyId}
            selectOnChange={this.selectOnChange}
            checkboxOnChange={this.checkboxOnChange}
            cancel={this.cancel}
          />
        )}

        <PaginatedList
          filterValue={this.state.filterValue}
          component={LineItemWrapper}
          items={allocatedAgencies}
          keys={['agencyId', 'agencyName']}
          fuzzyFilterFactory={this.fuzzyFilterFactory}
          pluralName="agencies"
        />
        <Row>
          <Col sm={12}>
            <CSSTransitionGroup
              transitionName="fade"
              transitionEnterTimeout={300}
              transitionLeaveTimeout={300}
            >
              {this.state.successMsg && (
                <p className="text-success">{this.state.successMsg}</p>
              )}
            </CSSTransitionGroup>
          </Col>
        </Row>
        {this.state.errorMsg && (
          <div className="alert alert-danger">{this.state.errorMsg}</div>
        )}
      </div>
    );
  }
}

export default CustomerAgencies;



// WEBPACK FOOTER //
// ./src/containers/customer/CustomerAgencies.js